import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { styled } from "~/application/theme";
import { Card } from "~/components/Card";
import { EmptyState } from "~/components/EmptyState";
import { Skeleton } from "~/components/Skeleton";
import { TotalSpentResponse } from "../types";
import { asCurrency, compactCurrency } from "../utils";
import { ChartTooltip } from "./ChartTooltip";

export interface SpendingChartProps {
  data?: TotalSpentResponse;
  isFetching: boolean;
}

const ChartContainer = styled("div", {
  width: "100%",
  height: "320px",
});

const StyledResponsiveContainer = styled(ResponsiveContainer, {
  "& .recharts-cartesian-grid-horizontal, & .recharts-cartesian-grid-vertical": {
    stroke: "$neutral-200",
  },
  "& .recharts-cartesian-axis-tick-value": {
    fill: "$neutrals-dark",
    fontSize: "$xs",
  },
});

// `stroke` is a raw SVG attribute on recharts elements and cannot resolve
// Stitches `$token` references — use the literal `primary-base` hex value.
const LINE_COLOR = "#0064c5";

export const SpendingChart = ({ data, isFetching }: SpendingChartProps) => {
  if (isFetching || !data) {
    return (
      <Card css={{ p: "$6" }}>
        <Skeleton variant="text-6" css={{ width: "40%" }} />
        <Skeleton css={{ width: "100%", height: "320px", mt: "$4" }} />
      </Card>
    );
  }

  if (data.series.length === 0) {
    return <EmptyState>Nenhum dado de gasto disponível para o período.</EmptyState>;
  }

  return (
    <Card css={{ p: "$6" }}>
      <ChartContainer>
        <StyledResponsiveContainer width="100%" height="100%">
          <LineChart data={data.series} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="period" tickLine={false} axisLine={false} tickMargin={8} />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={compactCurrency}
            />
            <Tooltip
              cursor={{ stroke: "#E4E4E7" }}
              content={<ChartTooltip valueFormatter={asCurrency} />}
            />
            <Line
              type="monotone"
              dataKey="totalSpent"
              name="Gasto Total"
              stroke={LINE_COLOR}
              strokeWidth={2}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </StyledResponsiveContainer>
      </ChartContainer>
    </Card>
  );
};

SpendingChart.displayName = "SpendingChart";
